// /frontend/src/pages/JobDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, CircularProgress, Alert, Paper, Chip, Button, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applicationStatus, setApplicationStatus] = useState({ message: '', error: false });

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/jobs/${id}`);
        setJob(res.data);
      } catch (err) {
        setError('Impossibile caricare i dettagli dell\'offerta di lavoro.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const handleApply = async () => {
    setApplicationStatus({ message: 'Invio candidatura...', error: false });
    try {
      const res = await axios.post(`/api/jobs/${id}/apply`);
      setApplicationStatus({ message: res.data.message, error: false });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Si è verificato un errore.';
      setApplicationStatus({ message: errorMessage, error: true });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!job) {
    return <Typography>Annuncio non trovato.</Typography>;
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Torna Indietro
      </Button>

      <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Titillium Web", sans-serif !important' }}>
        {job.title}
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        alignItems: 'center', 
        color: 'text.secondary', 
        mb: 2, 
        gap: 2,
        fontFamily: '"Titillium Web", sans-serif !important'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ mr: 1 }} /> {job.business.name}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOnIcon sx={{ mr: 1 }} /> {job.location}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ArticleIcon sx={{ mr: 1 }} /> {job.type}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarMonthIcon sx={{ mr: 1 }} /> 
          Pubblicato il {new Date(job.createdAt).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </Box>
      </Box>

      <Box sx={{ my: 2 }}>
        {job.categories.map(({ category }) => (
          <Chip key={category.name} label={category.name} sx={{ mr: 1, mb: 1, fontFamily: '"Titillium Web", sans-serif !important' }} />
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Titillium Web", sans-serif !important' }}>
        Descrizione
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap', fontFamily: '"Titillium Web", sans-serif !important' }}>
        {job.description}
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Titillium Web", sans-serif !important' }}>
        Requisiti
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap', fontFamily: '"Titillium Web", sans-serif !important' }}>
        {job.requirements}
      </Typography>
      
      {job.salary && (
        <>
          <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Titillium Web", sans-serif !important' }}>
            Retribuzione
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontFamily: '"Titillium Web", sans-serif !important' }}>
            {job.salary}
          </Typography>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        {job.hasApplied ? (
          <Chip label="Ti sei già candidato per questa posizione" color="success" />
        ) : (
          <Button 
            variant="contained" 
            size="large" 
            onClick={handleApply} 
            disabled={!!applicationStatus.message}
          >
            Candidati Ora
          </Button>
        )}
        {applicationStatus.message && !job.hasApplied && (
          <Alert severity={applicationStatus.error ? 'error' : 'success'}>
            {applicationStatus.message}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default JobDetail;